import { useEffect } from 'react';

interface FontConfig {
  loadGoogleFonts?: boolean;
  loadLocalFonts?: boolean;
  families?: string[];
  weights?: string[];
  display?: 'auto' | 'block' | 'swap' | 'fallback' | 'optional';
  text?: string;
}

const FontLoader = ({ 
  loadGoogleFonts = false,
  loadLocalFonts = false,
  families = ['Noto Sans:300,400,500,600,700', 'Nunito:300,400,500,600,700', 'Poppins:300,400,500,600,700,800,900'],
  weights = ['300', '400', '500', '600', '700'],
  display = 'swap',
  text 
}: FontConfig) => {
  useEffect(() => {
    if (!loadGoogleFonts) return;
    
    const initializeGoogleFonts = () => {
      const existingLink = document.querySelector('link[data-font-loader]');
      if (existingLink) return;

      const preconnectToGoogleFonts = () => {
        if (!document.querySelector('link[href="https://fonts.googleapis.com"]')) {
          const preconnect1 = document.createElement('link');
          preconnect1.rel = 'preconnect';
          preconnect1.href = 'https://fonts.googleapis.com';
          document.head.appendChild(preconnect1);
        }

        if (!document.querySelector('link[href="https://fonts.gstatic.com"]')) {
          const preconnect2 = document.createElement('link');
          preconnect2.rel = 'preconnect';
          preconnect2.href = 'https://fonts.gstatic.com';
          preconnect2.crossOrigin = 'anonymous';
          document.head.appendChild(preconnect2);
        }
      };

      const createFontLink = () => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        const fontParams = families.map(family => `family=${family.replace(/\s+/g, '+')}`).join('&');
        link.href = `https://fonts.googleapis.com/css2?${fontParams}&display=${display}`;
        link.setAttribute('data-font-loader', 'true');
        document.head.appendChild(link);
      };

      preconnectToGoogleFonts();
      
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', createFontLink);
      } else {
        createFontLink();
      }
    };

    initializeGoogleFonts();
  }, [loadGoogleFonts, families, weights, display, text]);

  return null;
};

const createLocalFontFace = (fontFamily: string, fontPath: string, weight: string = '400', style: string = 'normal'): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!('FontFace' in window)) {
      resolve();
      return;
    }

    const fontFace = new FontFace(fontFamily, `url(${fontPath})`, {
      weight,
      style,
      display: 'swap',
    });

    fontFace.load()
      .then(() => {
        document.fonts.add(fontFace);
        resolve();
      })
      .catch(error => {
        console.warn(`Failed to load local font ${fontFamily}:`, error);
        resolve();
      });
  });
};

const preloadLocalFonts = async () => {
  const localFonts = [
    { family: 'CircularStd', path: '/fonts/CircularStd-Book.woff', weight: '400' },
    { family: 'CircularStd', path: '/fonts/CircularStd-Bold.woff', weight: '700' },
    { family: 'CircularStd', path: '/fonts/CircularStd-Black.woff', weight: '900' },
  ];

  try {
    const fontPromises = localFonts.map(({ family, path, weight }) => 
      createLocalFontFace(family, path, weight)
    );
    
    await Promise.allSettled(fontPromises);
  } catch (error) {
    console.warn('Error loading local fonts:', error);
  }
};

const optimizeFontLoading = () => {
  if (document.querySelector('style[data-font-optimization]')) return;
  
  const style = document.createElement('style');
  style.setAttribute('data-font-optimization', 'true');
  style.textContent = `
    @font-face {
      font-family: 'system-ui-fallback';
      src: local('system-ui'), local('-apple-system'), local('BlinkMacSystemFont'), local('Segoe UI'), local('Roboto'), local('Helvetica Neue'), local('Arial'), local('sans-serif');
      font-display: block;
    }
    
    body {
      font-family: 'system-ui-fallback', 'Noto Sans', 'Nunito', 'Poppins', 'CircularStd', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    }
    
    .font-loading {
      font-family: 'system-ui-fallback', system-ui, -apple-system, sans-serif;
    }
  `;
  document.head.appendChild(style);
};

export { preloadLocalFonts, optimizeFontLoading };
export default FontLoader; 